// src/lib/qr/DataMatrixScanner.ts
import type { QRCodeScanner, QRLocation } from './QRCodeScanner';
import { BrowserDatamatrixCodeReader } from '@zxing/browser';
import type { Result } from '@zxing/library';

export class DataMatrixScanner implements QRCodeScanner {
  public qrLocation: QRLocation | null = null;

  private reader = new BrowserDatamatrixCodeReader();
  private offscreen: HTMLCanvasElement | null = null;
  private debugCanvas: HTMLCanvasElement | null = null; // <- dev-only

  setDebugCanvas(el: HTMLCanvasElement | null) {
    this.debugCanvas = el;
  }

  private getOffscreen(w: number, h: number): HTMLCanvasElement {
    if (!this.offscreen) this.offscreen = document.createElement('canvas');
    if (this.offscreen.width !== w) this.offscreen.width = w;
    if (this.offscreen.height !== h) this.offscreen.height = h;
    return this.offscreen;
  }

  private invertInto(target: HTMLCanvasElement, src: HTMLCanvasElement) {
    const tctx = target.getContext('2d');
    const sctx = src.getContext('2d');
    if (!tctx || !sctx) return;

    tctx.clearRect(0, 0, target.width, target.height);
    tctx.drawImage(src, 0, 0);

    const img = tctx.getImageData(0, 0, target.width, target.height);
    const data = img.data;
    for (let i = 0; i < data.length; i += 4) {
      data[i] = 255 - data[i];
      data[i + 1] = 255 - data[i + 1];
      data[i + 2] = 255 - data[i + 2];
    }
    tctx.putImageData(img, 0, 0);

    // Dev preview: mirror into external debug canvas if provided
    if (this.debugCanvas) {
      this.debugCanvas.width = target.width;
      this.debugCanvas.height = target.height;
      const dctx = this.debugCanvas.getContext('2d');
      dctx?.clearRect(0, 0, this.debugCanvas.width, this.debugCanvas.height);
      dctx?.drawImage(target, 0, 0);
    }
  }

  private async tryDecode(canvas: HTMLCanvasElement): Promise<Result | null> {
    try {
      const res = await this.reader.decodeFromCanvas(canvas);
      return res ?? null;
    } catch {
      return null;
    }
  }

  async scan(canvas: HTMLCanvasElement): Promise<string | null> {
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const direct = await this.tryDecode(canvas);
    if (direct) {
      this.qrLocation = null;
      return direct.getText();
    }

    const off = this.getOffscreen(canvas.width, canvas.height);
    this.invertInto(off, canvas);

    const inverted = await this.tryDecode(off);
    if (inverted) {
      this.qrLocation = null;
      return inverted.getText();
    }

    return null;
  }
}
